import api from '../../../axios/axios-api'
import {
  mapInformeRow,
  formatInformeFechaTabla,
} from '../../../utils/helpers/informesHomeMappers'
import { apiDateTimeToIsoLocal } from '../../../utils/helpers/portalDateFormat'
import {
  extractTurnosListFromApi,
  mapTurnoPacienteRow,
} from '../../../utils/helpers/turnosPacienteMappers'

let portalPrivateParamsInFlight = null
const TEXTO_SIN_FECHA = 'Sin fecha especificada'
const TEXTO_NO_ESPECIFICADO = 'No especificado'

function readNullableInt(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function applyActivePortalFicha(params, state) {
  if (state?.activePortalFichaId) params.fichaId = state.activePortalFichaId
  return params
}

function readMinutosAnticipacionTurnos(value) {
  const n = value != null && value !== '' ? Number(value) : 60
  if (!Number.isFinite(n)) return 60
  return Math.max(0, Math.trunc(n))
}

function mapLaboratorioRow(raw, index) {
  const infoLabId = readNullableInt(raw?.infoLabId)
  const informe = String(raw?.servNombre ?? '').trim() || '—'
  const fecha = raw?.mpFecha ?? null
  return {
    id: `${infoLabId ?? 'n'}-${index}`,
    informe,
    fechaDisplay: formatInformeFechaTabla(fecha),
    fechaSort: fecha,
    infoLabId,
  }
}

function mapOtroDocumentoRow(raw, index) {
  const asId = readNullableInt(raw?.asId)
  const servicio = String(raw?.servicio ?? '').trim()
  const titulo = String(raw?.titulo ?? '').trim()
  const informe = servicio || '—'
  const fecha = raw?.fecha ?? null
  return {
    id: `${asId ?? 'n'}-${index}`,
    informe,
    titulo,
    fechaDisplay: formatInformeFechaTabla(fecha),
    fechaSort: fecha,
    asId,
  }
}

function firstValue(raw, keys) {
  for (const key of keys) {
    const value = raw?.[key]
    if (value != null && value !== '') return value
  }
  return null
}

function textValue(raw, keys, fallback = 'â€”') {
  const value = firstValue(raw, keys)
  const text = String(value ?? '').trim()
  return text || fallback
}

function normalizePedidoMedicoLink(value) {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const markdownMatch = text.match(/^\[[^\]]+\]\((https?:\/\/[^)\s]+)\)$/i)
  if (markdownMatch?.[1]) return markdownMatch[1].trim()
  return text
}

// eslint-disable-next-line no-unused-vars
function mapAdmissionLegacyRow(raw, index) {
  const meId = readNullableInt(firstValue(raw, ['meId', 'id', 'admisionId', 'internacionId']))
  const fechaIngreso = firstValue(raw, [
    'fechaIngreso',
    'meFecha',
    'ingreso',
    'fechaAdmision',
  ])
  const fechaEgreso = firstValue(raw, [
    'fechaEgreso',
    'fechaEgr',
    'egreso',
    'fechaAlta',
  ])
  return {
    id: `${meId ?? 'n'}-${index}`,
    meId,
    internacion: textValue(raw, [
      'internacion',
      'tipoInternacion',
      'tipoInterValue',
      'tipo',
      'servicio',
    ]),
    informe: textValue(raw, [
      'internacion',
      'tipoInternacion',
      'tipoInterValue',
      'tipo',
      'servicio',
    ]),
    fechaDisplay: formatInformeFechaTabla(fechaIngreso),
    fechaSort: fechaIngreso,
    formato: 'PDF',
    fechaIngresoDisplay: formatInformeFechaTabla(fechaIngreso),
    fechaIngresoSort: fechaIngreso,
    fechaEgresoDisplay: fechaEgreso ? formatInformeFechaTabla(fechaEgreso) : 'â€”',
    fechaEgresoSort: fechaEgreso,
    sector: textValue(raw, ['sector', 'secNombre', 'sectorNombre']),
    cama: textValue(raw, ['cama', 'camaNombre', 'habitacion']),
    estado: textValue(raw, ['estado', 'estadoInternacion', 'situacion']),
  }
}

function mapAdmissionRow(raw, index) {
  const meId = readNullableInt(raw?.meId)
  const fechaDesde = raw?.fechaDesde ?? null
  const fechaHasta = raw?.fechaHasta ?? null
  const fechaEpicrisis = raw?.fechaEpicrisis ?? null
  return {
    id: `${meId ?? 'n'}-${index}`,
    meId,
    fichaId: readNullableInt(raw?.fichaId),
    servicio: textValue(raw, ['servicio', 'Servicio'], TEXTO_NO_ESPECIFICADO),
    nroInternacion: textValue(raw, ['nroInternacion'], TEXTO_NO_ESPECIFICADO),
    internacion: textValue(raw, ['servicio', 'Servicio'], TEXTO_NO_ESPECIFICADO),
    formato: 'PDF',
    textoSinFecha: TEXTO_SIN_FECHA,
    fechaDesde,
    fechaDesdeFormateada: textValue(
      raw,
      ['fechaDesdeFormateada'],
      fechaDesde ? formatInformeFechaTabla(fechaDesde) : 'Ã¢â‚¬â€',
    ),
    fechaHasta,
    fechaHastaFormateada: textValue(
      raw,
      ['fechaHastaFormateada'],
      fechaHasta ? formatInformeFechaTabla(fechaHasta) : 'Ã¢â‚¬â€',
    ),
    fechaEpicrisis,
    fechaEpicrisisFormateada: textValue(
      raw,
      ['fechaEpicrisisFormateada'],
      fechaEpicrisis ? formatInformeFechaTabla(fechaEpicrisis) : 'Ã¢â‚¬â€',
    ),
    prestador: textValue(raw, ['prestador']),
    especialidad: textValue(raw, ['especialidad']),
  }
}

function mapPedidoMedicoRow(raw, index) {
  const pedEstEId = readNullableInt(firstValue(raw, ['pedEstEId', 'PedEstEId', 'id']))
  const fecha = firstValue(raw, ['fecha', 'Fecha'])
  const hora = firstValue(raw, ['hora', 'Hora'])
  const practicasSource = firstValue(raw, ['practicas', 'Practicas'])
  const pedEstPLinksSource = firstValue(raw, ['pedEstPLinks', 'PedEstPLinks'])
  const pedEstPLinks = Array.isArray(pedEstPLinksSource)
    ? [
        ...new Set(
          pedEstPLinksSource
            .map(normalizePedidoMedicoLink)
            .filter(Boolean),
        ),
      ]
    : []
  const practicas = Array.isArray(practicasSource)
    ? practicasSource
        .map((item) => {
          if (typeof item === 'string') return item
          return firstValue(item, ['nombre', 'Nombre', 'nomNom', 'NomNom'])
        })
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    : []
  return {
    id: `${pedEstEId ?? 'n'}-${index}`,
    pedEstEId,
    fecha,
    hora,
    fechaFormateada: fecha ? formatInformeFechaTabla(fecha) : TEXTO_SIN_FECHA,
    tipoPedido: textValue(raw, ['tipoPedido', 'TipoPedido'], TEXTO_NO_ESPECIFICADO),
    servicio: textValue(raw, ['servicio', 'Servicio'], TEXTO_NO_ESPECIFICADO),
    prestador: textValue(raw, ['prestador', 'Prestador'], TEXTO_NO_ESPECIFICADO),
    estadoNombre: textValue(raw, ['estadoNombre', 'EstadoNombre'], TEXTO_NO_ESPECIFICADO),
    observacion: textValue(raw, ['observacion', 'Observacion'], ''),
    nroAtencion: textValue(raw, ['nroAtencion', 'NroAtencion'], ''),
    nroInternacion: textValue(raw, ['nroInternacion', 'NroInternacion'], ''),
    obraSocial: textValue(raw, ['obraSocial', 'ObraSocial'], ''),
    practicas,
    practicasEvaluadas: firstValue(raw, ['practicasEvaluadas', 'PracticasEvaluadas']) || [],
    pedEstPLinks,
    practicasTexto: practicas.length ? practicas.join(', ') : 'No especificadas',
    mostrarAccionSacarTurno:
      firstValue(raw, ['mostrarAccionSacarTurno', 'MostrarAccionSacarTurno']) !== false,
    puedeSacarTurno:
      firstValue(raw, ['puedeSacarTurno', 'PuedeSacarTurno']) === true,
    motivoNoPuedeSacarTurno: textValue(
      raw,
      ['motivoNoPuedeSacarTurno', 'MotivoNoPuedeSacarTurno'],
      'Este pedido médico no está disponible para sacar turno desde el portal.',
    ),
  }
}

function extractFileNameFromHeaders(headers, fallback = 'archivo') {
  const cd = headers?.['content-disposition']
  if (!cd || typeof cd !== 'string') return fallback
  const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, '')) || fallback
    } catch {
      return star[1].trim().replace(/^["']|["']$/g, '') || fallback
    }
  }
  const plain = cd.match(/filename\s*=\s*([^;]+)/i)
  if (plain?.[1]) {
    const s = plain[1].trim().replace(/^["']|["']$/g, '')
    return s || fallback
  }
  return fallback
}

export default {
  async fetchHomeMenu({ commit }) {
    commit('setHomeMenuLoading', true)
    commit('setHomeMenuError', null)
    try {
      const { data } = await api.get('Home/menu')
      commit('setHomeMenu', Array.isArray(data) ? data : [])
    } catch (e) {
      commit('setHomeMenu', [])
      commit('setHomeMenuError', e)
    } finally {
      commit('setHomeMenuLoading', false)
    }
  },

  async fetchHomeProfile({ commit }, payload = {}) {
    try {
      const params = {}
      const fichaId = Number(
        payload && Object.prototype.hasOwnProperty.call(payload, 'fichaId')
          ? payload.fichaId
          : null,
      )
      if (Number.isFinite(fichaId) && fichaId > 0) params.fichaId = fichaId
      const { data } = await api.get('Home/profile', { params })
      commit('setHomeProfile', {
        nombre: data?.nombre ?? '',
        apellido: data?.apellido ?? '',
      })
    } catch {
      commit('setHomeProfile', { nombre: '', apellido: '' })
    }
  },

  async fetchTurnosPendientes({ commit, state }) {
    commit('setTurnosPendientes', { loading: true, error: null })
    try {
      const params = {}
      if (state.activePortalFichaId) params.fichaId = state.activePortalFichaId
      const { data } = await api.get('Turnos/pendientes', { params })
      const list = extractTurnosListFromApi(data)
      const items = list.map((r, i) => mapTurnoPacienteRow(r, i))
      commit('setTurnosPendientes', {
        loading: false,
        items,
        error: null,
        fetched: true,
      })
    } catch (e) {
      commit('setTurnosPendientes', {
        loading: false,
        items: [],
        error: e,
        fetched: true,
      })
    }
  },

  async fetchTurnosHistorialPaciente(
    { commit, state },
    { fechaDesde, fechaHasta } = {},
  ) {
    commit('setTurnosHistorial', { loading: true, error: null })
    try {
      const params = {}
      if (state.activePortalFichaId) params.fichaId = state.activePortalFichaId
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Turnos/historial-paciente', { params })
      const list = extractTurnosListFromApi(data)
      const items = list.map((r, i) => mapTurnoPacienteRow(r, i, { esHistorial: true }))
      commit('setTurnosHistorial', {
        loading: false,
        items,
        error: null,
        fetched: true,
      })
    } catch (e) {
      commit('setTurnosHistorial', {
        loading: false,
        items: [],
        error: e,
        fetched: true,
      })
    }
  },

  async cancelarTurnoPendiente(_store, { turnoId }) {
    const id = Number(turnoId)
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error('Identificador de turno no válido.')
    }
    await api.post(`Turnos/${id}/cancelar`)
  },

  async fetchPreparacionTurnoPendiente({ state }, { turnoId, fichaId } = {}) {
    const id = Number(turnoId)
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error('Identificador de turno no valido.')
    }
    const params = applyActivePortalFicha({}, state)
    const fichaTurnoId = Number(fichaId)
    if (Number.isFinite(fichaTurnoId) && fichaTurnoId > 0) {
      params.fichaId = fichaTurnoId
    }
    const { data } = await api.get(`Turnos/${id}/preparacion`, {
      params,
      skipGlobalErrorAlert: true,
    })
    return data
  },

  async fetchHomeInitialInfo({ commit, state }) {
    try {
      const params = applyActivePortalFicha({}, state)
      const { data } = await api.get('Home/initial-info', { params })
      commit('setHomeInitialInfo', {
        cantTurnos: Number(data?.cantTurnos ?? 0) || 0,
        cantInformesDiagImg: Number(data?.cantInformesDiagImg ?? 0) || 0,
        cantInformesLab: Number(data?.cantInformesLab ?? 0) || 0,
        otrosDocumentos: Number(data?.otrosDocumentos ?? data?.otrosEstudios ?? 0) || 0,
        cantEpicrisis: Number(data?.cantEpicrisis ?? 0) || 0,
      })
    } catch {
      commit('setHomeInitialInfo', {
        cantTurnos: 0,
        cantInformesDiagImg: 0,
        cantInformesLab: 0,
        otrosDocumentos: 0,
        cantEpicrisis: 0,
      })
    }
  },

  async fetchInformesUltimos({ commit, state }) {
    commit('setInformesHome', { loading: true, error: null })
    try {
      const params = applyActivePortalFicha({}, state)
      const { data } = await api.get('Informes/ultimos', { params })
      const list = Array.isArray(data) ? data : []
      commit('setInformesHome', {
        loading: false,
        items: list.map((r, i) => mapInformeRow(r, i)),
        error: null,
      })
    } catch (e) {
      commit('setInformesHome', { loading: false, items: [], error: e })
    }
  },

  async fetchInformesDiagnosticos(
    { commit, state },
    { fechaDesde, fechaHasta, nombreInforme } = {},
  ) {
    commit('setInformesHome', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Informes/diagnosticos', { params })
      let rows = Array.isArray(data) ? data : []
      rows = rows.map((r, i) => mapInformeRow(r, i))
      const q = String(nombreInforme ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          String(r.informe ?? '')
            .toLowerCase()
            .includes(q),
        )
      }
      commit('setInformesHome', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setInformesHome', { loading: false, items: [], error: e })
    }
  },

  async fetchInformesDiagnosticosPage(
    { commit, state },
    { fechaDesde, fechaHasta, nombreInforme } = {},
  ) {
    commit('setInformesDiagnosticosPage', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Informes/diagnosticos', { params })
      let rows = Array.isArray(data) ? data : []
      rows = rows.map((r, i) => mapInformeRow(r, i))
      const q = String(nombreInforme ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          String(r.informe ?? '')
            .toLowerCase()
            .includes(q),
        )
      }
      commit('setInformesDiagnosticosPage', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setInformesDiagnosticosPage', {
        loading: false,
        items: [],
        error: e,
      })
    }
  },

  async fetchInformesLaboratorioPage(
    { commit, state },
    { fechaDesde, fechaHasta, nombreInforme } = {},
  ) {
    commit('setResultadosLaboratorioPage', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Informes/laboratorio', { params })
      let rows = (Array.isArray(data) ? data : []).map((r, i) => mapLaboratorioRow(r, i))
      const q = String(nombreInforme ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          String(r.informe ?? '')
            .toLowerCase()
            .includes(q),
        )
      }
      commit('setResultadosLaboratorioPage', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setResultadosLaboratorioPage', {
        loading: false,
        items: [],
        error: e,
      })
    }
  },

  async fetchInformesLaboratorioUrlExterna({ state }) {
    const params = applyActivePortalFicha({}, state)
    const { data } = await api.get('Informes/laboratorio/url-externa', { params })
    const raw = data?.urlExterna ?? data?.url
    return raw != null && String(raw).trim() ? String(raw).trim() : null
  },

  async fetchInformeLaboratorioPdfLisBlob({ state }, infoLabId) {
    const params = applyActivePortalFicha({}, state)
    const res = await api.get(`Informes/laboratorio/${infoLabId}/pdf-lis`, {
      params,
      responseType: 'blob',
    })
    return new Blob([res.data], { type: 'application/pdf' })
  },

  async fetchOtrosDocumentosPage(
    { commit, state },
    { fechaDesde, fechaHasta, nombreInforme } = {},
  ) {
    commit('setOtrosDocumentosPage', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Informes/otros', { params })
      let rows = (Array.isArray(data) ? data : []).map((r, i) => mapOtroDocumentoRow(r, i))
      const q = String(nombreInforme ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          [r.informe, r.titulo].some((value) =>
            String(value ?? '').toLowerCase().includes(q),
          ),
        )
      }
      commit('setOtrosDocumentosPage', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setOtrosDocumentosPage', {
        loading: false,
        items: [],
        error: e,
      })
    }
  },

  async fetchAdmissionsPage(
    { commit, state },
    { fechaDesde, fechaHasta, nombreInternacion } = {},
  ) {
    commit('setAdmissionsPage', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('Internado/epicrisis-disponibles', { params })
      let rows = (Array.isArray(data) ? data : []).map((r, i) => mapAdmissionRow(r, i))
      const q = String(nombreInternacion ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          [r.servicio, r.prestador, r.especialidad].some((value) =>
            String(value ?? '')
              .toLowerCase()
              .includes(q),
          ),
        )
      }
      commit('setAdmissionsPage', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setAdmissionsPage', {
        loading: false,
        items: [],
        error: e,
      })
    }
  },

  async fetchPedidosMedicosPage(
    { commit, state },
    { fechaDesde, fechaHasta, busqueda } = {},
  ) {
    commit('setPedidosMedicosPage', { loading: true, error: null })
    try {
      const params = {}
      applyActivePortalFicha(params, state)
      if (fechaDesde) params.fechaDesde = fechaDesde
      if (fechaHasta) params.fechaHasta = fechaHasta
      const { data } = await api.get('PedidosMedicos', { params })
      let rows = (Array.isArray(data) ? data : []).map((r, i) => mapPedidoMedicoRow(r, i))
      const q = String(busqueda ?? '').trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) =>
          [
            r.servicio,
            r.prestador,
            r.tipoPedido,
            r.estadoNombre,
            r.observacion,
            r.nroInternacion,
          ].some((value) =>
            String(value ?? '')
              .toLowerCase()
              .includes(q),
          ),
        )
      }
      commit('setPedidosMedicosPage', {
        loading: false,
        items: rows,
        error: null,
      })
    } catch (e) {
      commit('setPedidosMedicosPage', {
        loading: false,
        items: [],
        error: e,
      })
    }
  },

  async fetchOtroDocumentoArchivoBlob({ state }, asId) {
    const params = applyActivePortalFicha({}, state)
    const res = await api.get(`Informes/otros/${asId}/archivo`, {
      params,
      responseType: 'blob',
    })
    return {
      blob: new Blob([res.data], {
        type: res.headers?.['content-type'] || res.data?.type || 'application/octet-stream',
      }),
      fileName: extractFileNameFromHeaders(res.headers, `archivo-${asId}`),
    }
  },

  async fetchPortalPrivateParams({ commit, state }, { force = false } = {}) {
    if (portalPrivateParamsInFlight) return portalPrivateParamsInFlight
    if (!force && state.portalPrivateParams.fetched && state.portalPrivateParams.error == null) {
      return
    }
    portalPrivateParamsInFlight = (async () => {
      commit('setPortalPrivateParams', { loading: true, error: null })
      try {
        const { data } = await api.get('Config/private')
        const iso = apiDateTimeToIsoLocal(data?.fechaDesdeConsInf)
        const urlExterna = String(data?.urlExternaResultadosLaboratorio ?? '').trim()
        const validarAfiliado = data?.validarAfiliadoAutogestion ?? null
        const pacienteObsTurnos =
          data?.pacienteCargaObservacionesTurnosAutogestion ?? null
        const limiteRaw = data?.limiteAgendaTurnos ?? null
        const limiteNum =
          limiteRaw != null && limiteRaw !== '' ? Number(limiteRaw) : null
        const horasValidacionTurnoWebRaw =
          data?.horasValidacionTurnoWeb ?? data?.HorasValidacionTurnoWeb
        const horasValidacionTurnoWebNum =
          horasValidacionTurnoWebRaw != null &&
          horasValidacionTurnoWebRaw !== ''
            ? Number(horasValidacionTurnoWebRaw)
            : null
        const mesesMargenRaw =
          data?.mesesMargenCronogramasVigentesConsultaWeb ??
          data?.MesesMargenCronogramasVigentesConsultaWeb ??
          null
        const mesesMargenNum =
          mesesMargenRaw != null && mesesMargenRaw !== ''
            ? Number(mesesMargenRaw)
            : null
        const minutosAnticipacionTurnos = readMinutosAnticipacionTurnos(
          data?.minutosAnticipacionTurnos ?? data?.MinutosAnticipacionTurnos,
        )
        const celOblRaw = data?.celularObligatorioTurno
        const telOblRaw = data?.telObligTurnos
        const mostrarDatosFicha =
          data?.mostrarDatosFichaTurnosWeb ?? data?.MostrarDatosFichaTurnosWeb
        const usaModeloIAPP =
          data?.usaModeloIAPP ?? data?.UsaModeloIAPP
        const permiteAdjuntarArchivo =
          data?.permiteAdjuntarArchivo ?? data?.PermiteAdjuntarArchivo
        const permiteTurnoDesdePedidoMedico =
          data?.permiteTurnoDesdePedidoMedico ?? data?.PermiteTurnoDesdePedidoMedico
        const permiteSacarTurno =
          data?.permiteSacarTurno ?? data?.PermiteSacarTurno
        const permiteTurnoMultiPracticas =
          data?.permiteTurnoMultiPracticas ?? data?.PermiteTurnoMultiPracticas
        const diasVencimientoPedidoMedicoRaw =
          data?.diasVencimientoPedidoMedico ?? data?.DiasVencimientoPedidoMedico
        const diasVencimientoPedidoMedicoNum =
          diasVencimientoPedidoMedicoRaw != null &&
          diasVencimientoPedidoMedicoRaw !== ''
            ? Number(diasVencimientoPedidoMedicoRaw)
            : null
        const cantidadMaximaArchivosAdjuntosRaw =
          data?.cantidadMaximaArchivosAdjuntos ??
          data?.CantidadMaximaArchivosAdjuntos
        const cantidadMaximaArchivosAdjuntosNum =
          cantidadMaximaArchivosAdjuntosRaw != null &&
          cantidadMaximaArchivosAdjuntosRaw !== ''
            ? Number(cantidadMaximaArchivosAdjuntosRaw)
            : 1
        const telefonoTurnoWeb = String(data?.telefonoTurnoWeb ?? '').trim()
        const emailDeNotificacionesCancelacionClienteWeb = String(
          data?.emailDeNotificacionesCancelacionClienteWeb ?? ''
        ).trim()
        const mjeNotaInicial = String(data?.mjeNotaInicial ?? '').trim()
        const mjePacienteInexistente = String(
          data?.mjePacienteInexistente ?? ''
        ).trim()
        const mjePacienteSinOsDisponibleWeb = String(
          data?.mjePacienteSinOsDisponibleWeb ?? ''
        ).trim()
        const noConfirmarAutomaticamenteTurnos =
          data?.noConfirmarAutomaticamenteTurnos ??
          data?.NoConfirmarAutomaticamenteTurnos
        const validaTurnoWeb =
          data?.validaTurnoWeb ?? data?.ValidaTurnoWeb
        const portalPacienteImplementado =
          data?.portalPacienteImplementado === true
        const autogestionImplementada =
          data?.autogestionImplementada === true
        commit('setPortalPrivateParams', {
          loading: false,
          fetched: true,
          urlExternaResultadosLaboratorio: urlExterna,
          fechaDesdeConsInfIso: iso,
          validarAfiliadoAutogestion:
            typeof validarAfiliado === 'boolean' ? validarAfiliado : null,
          pacienteCargaObservacionesTurnosAutogestion:
            typeof pacienteObsTurnos === 'boolean' ? pacienteObsTurnos : null,
          limiteAgendaTurnos:
            limiteNum != null && Number.isFinite(limiteNum) ? limiteNum : null,
          horasValidacionTurnoWeb:
            horasValidacionTurnoWebNum != null &&
            Number.isFinite(horasValidacionTurnoWebNum) &&
            horasValidacionTurnoWebNum > 0
              ? horasValidacionTurnoWebNum
              : null,
          mesesMargenCronogramasVigentesConsultaWeb:
            mesesMargenNum != null && Number.isFinite(mesesMargenNum)
              ? mesesMargenNum
              : null,
          minutosAnticipacionTurnos,
          telefonoTurnoWeb,
          emailDeNotificacionesCancelacionClienteWeb,
          mjeNotaInicial,
          mjePacienteInexistente,
          mjePacienteSinOsDisponibleWeb,
          noConfirmarAutomaticamenteTurnos:
            typeof noConfirmarAutomaticamenteTurnos === 'boolean'
              ? noConfirmarAutomaticamenteTurnos
              : null,
          validaTurnoWeb:
            typeof validaTurnoWeb === 'boolean' ? validaTurnoWeb : null,
          celularObligatorioTurno:
            typeof celOblRaw === 'boolean' ? celOblRaw : null,
          telObligTurnos: typeof telOblRaw === 'boolean' ? telOblRaw : null,
          mascaraCelTurno: String(data?.mascaraCelTurno ?? '').trim() || null,
          mascaraTelTurno: String(data?.mascaraTelTurno ?? '').trim() || null,
          actualizaFichaDesdeTurnoAutogestion:
            typeof data?.actualizaFichaDesdeTurnoAutogestion === 'boolean'
              ? data.actualizaFichaDesdeTurnoAutogestion
              : null,
          mostrarDatosFichaTurnosWeb:
            typeof mostrarDatosFicha === 'boolean' ? mostrarDatosFicha : null,
          usaModeloIAPP:
            typeof usaModeloIAPP === 'boolean'
              ? usaModeloIAPP
              : null,
          permiteAdjuntarArchivo:
            typeof permiteAdjuntarArchivo === 'boolean'
              ? permiteAdjuntarArchivo
              : null,
          cantidadMaximaArchivosAdjuntos:
            Number.isFinite(cantidadMaximaArchivosAdjuntosNum) &&
            cantidadMaximaArchivosAdjuntosNum > 0
              ? cantidadMaximaArchivosAdjuntosNum
              : 1,
          permiteTurnoDesdePedidoMedico:
            permiteTurnoDesdePedidoMedico === true,
          permiteSacarTurno: permiteSacarTurno === true,
          permiteTurnoMultiPracticas:
            permiteTurnoMultiPracticas === true,
          diasVencimientoPedidoMedico:
            Number.isFinite(diasVencimientoPedidoMedicoNum)
              ? diasVencimientoPedidoMedicoNum
              : null,
          portalPacienteImplementado,
          autogestionImplementada,
          error: null,
        })
      } catch (e) {
        commit('setPortalPrivateParams', {
          loading: false,
          fetched: true,
          urlExternaResultadosLaboratorio: '',
          fechaDesdeConsInfIso: '',
          validarAfiliadoAutogestion: null,
          pacienteCargaObservacionesTurnosAutogestion: null,
          limiteAgendaTurnos: null,
          horasValidacionTurnoWeb: null,
          mesesMargenCronogramasVigentesConsultaWeb: null,
          minutosAnticipacionTurnos: 60,
          telefonoTurnoWeb: '',
          emailDeNotificacionesCancelacionClienteWeb: '',
          mjeNotaInicial: '',
          mjePacienteInexistente: '',
          mjePacienteSinOsDisponibleWeb: '',
          noConfirmarAutomaticamenteTurnos: null,
          validaTurnoWeb: null,
          celularObligatorioTurno: null,
          telObligTurnos: null,
          mascaraCelTurno: null,
          mascaraTelTurno: null,
          actualizaFichaDesdeTurnoAutogestion: null,
          mostrarDatosFichaTurnosWeb: null,
          usaModeloIAPP: null,
          permiteAdjuntarArchivo: null,
          cantidadMaximaArchivosAdjuntos: 1,
          permiteTurnoDesdePedidoMedico: false,
          permiteSacarTurno: false,
          permiteTurnoMultiPracticas: false,
          diasVencimientoPedidoMedico: null,
          portalPacienteImplementado: false,
          autogestionImplementada: false,
          error: e,
        })
      } finally {
        portalPrivateParamsInFlight = null
      }
    })()
    return portalPrivateParamsInFlight
  },

  async fetchInformeEstudioUrlImagen({ state }, mpId) {
    const params = applyActivePortalFicha({ mpId }, state)
    const { data } = await api.get('Informes/diagnosticos/estudio-url-imagen', {
      params,
    })
    const url = data?.url
    return url != null && String(url).trim() ? String(url).trim() : null
  },

  async fetchInformePdfBlob({ state }, infoId) {
    const params = applyActivePortalFicha({}, state)
    const res = await api.get(`Informes/diagnosticos/${infoId}/pdf`, {
      params,
      responseType: 'blob',
    })
    return new Blob([res.data], { type: 'application/pdf' })
  },

  async fetchEpicrisisPdfBlob({ state }, meId) {
    const params = applyActivePortalFicha({}, state)
    const res = await api.get(`Epicrisis/${meId}/pdf`, {
      params,
      responseType: 'blob',
    })
    return new Blob([res.data], { type: 'application/pdf' })
  },
}
